import { useInfiniteQuery } from "@tanstack/react-query";
import { UserReviewType } from "../components/user/Profile/components/UserReviewModal/UserReviewsSegment";
import {
  fetchHiddenReviewsList,
  fetchPublicReviewsList,
} from "../services/reviews";

interface UseUserReviewsQueryOptions {
  enabled?: boolean;
}

export type ReviewsCounts = {
  fromSellers: number;
  fromBuyers: number;
};

export const useUserReviewsQuery = (
  type: UserReviewType,
  limit: number,
  userId?: string,
  options?: UseUserReviewsQueryOptions
) => {
  return useInfiniteQuery({
    queryKey: [`reviews-${type}-infinite`, limit, userId],
    queryFn: ({ pageParam = { limit, offset: 0 } }) => {
      const fetcher =
        type === UserReviewType.fromSellers
          ? fetchHiddenReviewsList
          : fetchPublicReviewsList;

      if (!userId) throw new Error("User ID is required");

      return fetcher(
        {
          limit: pageParam.limit,
          offset: pageParam.offset,
        },
        userId
      );
    },
    initialPageParam: { limit: limit, offset: 0 },
    getNextPageParam: (lastPage, allPages) => {
      const totalLoaded = allPages.reduce(
        (acc, page) => acc + page.list.length,
        0
      );
      const hasMore = lastPage.list.length === limit;
      return hasMore ? { limit: limit, offset: totalLoaded } : undefined;
    },
    retry: 0,
    enabled: options?.enabled ?? true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

export async function getUserReviewsCounts(
  userId: string
): Promise<ReviewsCounts> {
  const [sellers, buyers] = await Promise.all([
    fetchHiddenReviewsList({ limit: 1, offset: 0 }, userId),
    fetchPublicReviewsList({ limit: 1, offset: 0 }, userId),
  ]);

  return {
    fromSellers: Number(sellers.totalCount) || 0,
    fromBuyers: Number(buyers.totalCount) || 0,
  };
}
